'use client'

import React, { useMemo, useState } from 'react'

import type { ImageCardBlock as ImageCardBlockType } from '@/payload-types'

import { Media } from '@/components/Media'
import { CMSLink } from '@/components/Link'
import { Reveal } from '@/components/Reveal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * Guides directory filter + card grid (page-specific, /guides only).
 *
 * Figma Guides & Tools Index (4112:154104 desktop / 4112:154147 tablet /
 * 4112:154191 mobile):
 *   • The filter is the single 160×28 select Trigger reading "All Categories"
 *     (4112:159136) — the canonical Select, same as the Insights index. An earlier
 *     build used a tab-chip row, which the Figma frames do not contain.
 *   • Each card is Content Card 4112:158905: square image, gap/gap-6 24, then the
 *     guide title in Headings/headlineSm (Merriweather 24/32 −0.1). No description
 *     line and no separate underlined link — the whole card is the link.
 *
 * Guides have no real category field in the CMS, so filtering is keyword-driven
 * off the card title (matching the tab behaviour it replaces).
 */

const CATEGORIES = ['All Categories', 'Sell', 'Buy', 'Rent', 'Manage', 'Commercial'] as const
type Category = (typeof CATEGORIES)[number]

const CATEGORY_RULES: { cat: Exclude<Category, 'All Categories'>; keywords: string[] }[] = [
  { cat: 'Sell', keywords: ['sell', 'seller', 'auction'] },
  { cat: 'Buy', keywords: ['buy', 'buyer', 'first home', 'investment'] },
  { cat: 'Rent', keywords: ['rent', 'renting', 'tenant'] },
  { cat: 'Manage', keywords: ['manage', 'management', 'landlord'] },
  { cat: 'Commercial', keywords: ['commercial'] },
]

function categoryFor(title?: string | null): Exclude<Category, 'All Categories'> | null {
  const t = (title || '').toLowerCase()
  for (const rule of CATEGORY_RULES) {
    if (rule.keywords.some((k) => t.includes(k))) return rule.cat
  }
  return null
}

type Card = NonNullable<ImageCardBlockType['cards']>[number]

/**
 * Full directory section: select filter + the filtered card grid. Replaces the
 * shared ImageCardBlock render on /guides so filtering can drive the grid.
 */
export const GuidesDirectory: React.FC<{ cards?: ImageCardBlockType['cards'] }> = ({ cards }) => {
  const [active, setActive] = useState<Category>('All Categories')

  const items = useMemo(() => (cards ?? []) as Card[], [cards])

  const visible = useMemo(() => {
    if (active === 'All Categories') return items
    return items.filter((c) => categoryFor((c as any)?.title) === active)
  }, [items, active])

  if (items.length === 0) return null

  return (
    <>
      {/* Filter row — Figma Frame 455 (4112:159132): one 160×28 select trigger,
          64px band padding above the grid. */}
      <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] pb-[var(--spacing-section-sm)]">
        <Select onValueChange={(v) => setActive(v as Category)} value={active}>
          <SelectTrigger
            aria-label="Filter guides by category"
            className="w-40 data-[placeholder]:text-muted-foreground"
          >
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((label) => (
              <SelectItem key={label} value={label}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Card grid — Figma Frame 454 (4112:154127): 4 × 320 with 64px gaps at
          desktop, 2-up at tablet (32 col gap), 1-up at mobile; rows 64 apart. */}
      <div className="bg-background pb-[var(--spacing-section-md)]">
        <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)]">
          <div className="grid grid-cols-1 gap-y-12 md:grid-cols-2 md:gap-x-8 md:gap-y-16 lg:grid-cols-4 lg:gap-x-16">
            {visible.map((card, index) => {
              const { media, title, enableLink, link } = card as any

              const inner = (
                <>
                  {/* loading-ground: branded placeholder behind the cover image
                      while it loads (same treatment as listing cards). */}
                  <div className="loading-ground relative aspect-square overflow-hidden">
                    {media && typeof media === 'object' && (
                      <Media
                        fill
                        imgClassName="object-cover transition-transform duration-500 group-hover:scale-105"
                        resource={media}
                      />
                    )}
                  </div>
                  {/* Content Card title — Headings/headlineSm (Merriweather 400,
                      24/32 −0.1 via text-display-lg). */}
                  {title && (
                    <h3 className="font-display font-normal text-display-lg text-foreground">
                      {title}
                    </h3>
                  )}
                </>
              )

              // Steve 2026-08-24 — per-card slideup reveal, staggered by row
              // position (4-column grid at desktop), same treatment as the
              // Insights and Neighbourhoods directory grids.
              return (
                <Reveal key={index} variant="slideup" delay={(index % 4) * 100}>
                  {enableLink && link ? (
                    <CMSLink
                      {...link}
                      appearance="inline"
                      className="group flex flex-col gap-6"
                      label={null}
                    >
                      {inner}
                    </CMSLink>
                  ) : (
                    <div className="flex flex-col gap-6">{inner}</div>
                  )}
                </Reveal>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}
